import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TextField,
  Button,
  MenuItem,
  FormControl,
  Switch,
  Autocomplete,
  Box,
  InputAdornment,
  Typography,
} from "@mui/material";
import { GENDER_OPTIONS, SIZE_OPTIONS } from "../../../constants/optionsConfig";
import { COUNTRIES } from "../../../constants/countryCode";
import downarrow from "../../../assets/icons/down-arrow1.svg";
import SelectFeild from "../Select";
import TextFeild from "../TextFeild";

const ProfileForm = ({
  onSubmit,
  schema,
  defaultValues,
  submitButtonText,
  sizes,
  isEdit = false,
  profileImage,
  setProfileImage,
  profileicon,
  showNameField = true,
  showAgeField = true,
  showGenderField = true,
  showSizeField = true,
  showHideSizeField = true,
  showMobileField = true,
  showLocationField = true,
  showProfileImage = true,
  PROFILE_PAGE,
  INPUT_LABELS,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    // Reset form with new defaultValues when they change (for async data loading)
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    // Set default US country code if not already set
    if (!defaultValues?.country_code) {
      setValue(
        "country_code",
        COUNTRIES.find((country) => country.dial_code === "+1")
      );
    } else if (typeof defaultValues?.country_code === "string") {
      // Handle case when backend returns just the dial code string (e.g., "+1")
      const country = COUNTRIES.find((c) => c.dial_code === defaultValues.country_code);
      if (country) {
        setValue("country_code", country);
      }
    }
  }, [setValue, defaultValues]);

  const handleFormSubmit = (data) => {
    const capitalizeToogle =
      String(data?.toggle_hide_size || "")
        .charAt(0)
        .toUpperCase() +
      String(data?.toggle_hide_size || "")
        .slice(1)
        .toLowerCase();

    const dataToSend = {
      username: data?.name,
      clothing_preference:
        data?.clothing_preference || data?.clothingPreference,
      sizes: data?.sizes,
      age: data?.age,
      country_code: data?.country_code?.dial_code,
      toggle_hide_size: capitalizeToogle,
      mobile_number: data?.mobile_number,
      profile_picture: (profileImage === profileicon || profileImage?.includes('profile-new.svg')) ? null : profileImage,
      location: data?.location,
    };
    
    onSubmit(dataToSend);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Name Field */}
      {showNameField && (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextFeild
                id="name"
                label={INPUT_LABELS?.name}
                placeholder="Enter your name"
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.name}
                helperText={errors.name?.message}
                required
                tooltip
                tooltipText="Used for reviews"
                {...field}
              />
            )}
          />
        </Box>
      )}

      {/* Age Field */}
      {showAgeField && (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="age"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextFeild
                id="age"
                type="number"
                label={INPUT_LABELS?.age}
                placeholder="Enter your age"
                variant="outlined"
                fullWidth
                margin="normal"
                error={!!errors.age}
                helperText={errors.age?.message}
                {...field}
              />
            )}
          />
        </Box>
      )}

      {/* Gender Field */}
      {showGenderField && (
        <Box sx={{ marginTop: "10px", mb: "16px" }}>
          <Typography
            sx={{
              fontFamily: "Lexend, sans-serif",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "15px",
              color: "rgb(5, 5, 5)",
              mb: '5px'
            }}
          >
            {PROFILE_PAGE.clothingPreference}
          </Typography>
          <Controller
            name="clothingPreference"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div className="custom-radio-group">
                {GENDER_OPTIONS.map((option) => (
                  <div
                    key={option.key}
                    className={`custom-radio ${field.value === option.key ? "selected" : ""}`}
                    onClick={() => field.onChange(option.key)}
                  >
                    {option.value}
                  </div>
                ))}
              </div>
            )}
          />
          {errors.clothing_preference && (
            <Typography color="error" variant="caption">
              {errors.clothing_preference.message}
            </Typography>
          )}
        </Box>
      )}

      {/* Size Field */}
      {showSizeField && (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="sizes"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <SelectFeild
                {...field}
                id="sizes"
                label="Sizes"
                placeholder={PROFILE_PAGE.enterYourSize}
                options={
                  sizes.length > 0
                    ? sizes?.map((size) => size?.label)
                    : SIZE_OPTIONS?.map((size) => size?.value)
                }
                value={
                  sizes.length > 0
                    ? sizes?.find((size) => size?.id === field.value)?.label ||
                      ""
                    : SIZE_OPTIONS?.find((size) => size?.key === field.value)
                        ?.value || ""
                }
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  if (sizes.length > 0) {
                    // Use API data with size ID
                    const selectedSize = sizes?.find(
                      (size) => size?.label === selectedValue,
                    );
                    field.onChange(selectedSize?.id || "");
                  } else {
                    // Fallback to static options
                    const selectedKey = SIZE_OPTIONS.find(
                      (size) => size.value === selectedValue,
                    )?.key;
                    field.onChange(selectedKey || "");
                  }
                }}
                error={!!errors.sizes}
                helperText={errors.sizes?.message}
              />
            )}
          />
        </Box>
      )}

      {/* Hide Size Field */}
      {showHideSizeField && (
        <Box className="switch-container">
          <Box className="custom-switch-label">
            <Controller
              name="toggle_hide_size"
              control={control}
              defaultValue="false"
              render={({ field }) => (
                <>
                  <Switch
                    {...field}
                    className={`custom-switch ${field.value === "true" ? "active" : ""}`}
                    onChange={(event) =>
                      field.onChange(event.target.checked ? "true" : "false")
                    }
                  />
                  <span className="custom-switch-text">
                    {PROFILE_PAGE.hideSize}
                  </span>
                </>
              )}
            />
          </Box>
        </Box>
      )}

      {/* Mobile Number and Country Code */}
      {showMobileField && (
        <Box>
          <FormControl fullWidth margin="normal">
            <Typography
              variant="subtitle3"
              sx={{
                fontFamily: "Lexend, sans-serif",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "15px",
                color: "rgb(5, 5, 5)",
              }}
            >
              {PROFILE_PAGE.mobileNumber}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Controller
                name="country_code"
                control={control}
                defaultValue={COUNTRIES.find((country) => country.dial_code === "+1")}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={COUNTRIES}
                    getOptionLabel={(option) => option?.dial_code || ""}
                    sx={{ 
                      width: 120,
                      "& .MuiInputBase-root": {
                        height: "40px !important",
                      },
                      "& .MuiInputBase-input": {
                        height: "40px !important",
                        padding: "10px 0px 10px 0px !important",
                        boxSizing: "border-box !important",
                      },
                    }}
                    popupIcon={
                      <img
                        src={downarrow}
                        alt="Dropdown Icon"
                        style={{ width: 16, height: 16 }}
                      />
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: field.value?.flag ? (
                            <InputAdornment position="start">
                              <img
                                src={field.value.flag}
                                alt={field.value.name}
                                style={{ width: 24, height: 18 }}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            height: "40px !important",
                          },
                          "& .MuiInputBase-input": {
                            height: "40px !important",
                            padding: "10px 0px 10px 0px !important",
                            boxSizing: "border-box !important",
                          },
                          ".MuiSvgIcon-root": {
                            display: "none",
                          },
                        }}
                      />
                    )}
                    onChange={(_, newValue) => {
                      field.onChange(newValue);
                    }}
                    renderOption={(props, option) => (
                      <MenuItem {...props}>
                        <img
                          src={option.flag}
                          alt={option.name}
                          style={{ width: 20, height: 15, marginRight: 10 }}
                        />
                        {option.dial_code}
                      </MenuItem>
                    )}
                    value={field.value || null}
                  />
                )}
              />

              <Controller
                name="mobile_number"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="tel"
                    placeholder={PROFILE_PAGE.enterYourNumber}
                    error={!!errors.mobile_number}
                    helperText={errors.mobile_number?.message}
                    inputProps={{
                      pattern: "[0-9]*",
                      maxLength: 13,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: "40px !important",
                      },
                      "& .MuiInputBase-input": {
                        height: "40px !important",
                        padding: "10px 14px !important",
                        boxSizing: "border-box !important",
                      },
                      "& .MuiFormHelperText-root": {
                        margin: "4px 0 0 0 !important",
                        fontSize: "12px !important",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        isNaN(Number(e.key))
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </Box>
          </FormControl>
        </Box>
      )}

      {/* Location Field */}
      {showLocationField && (
        <Box sx={{ mb: 2 }}>
          <Controller
            name="location"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextFeild
                id="location"
                label="Your Location"
                {...field}
                placeholder={PROFILE_PAGE.enterYourLocation}
                fullWidth
                margin="normal"
                error={!!errors.location}
                helperText={errors.location?.message}
                sx={{ marginTop: "5px" }}
              />
            )}
          />
        </Box>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          aria-label="Submit profile changes"
          sx={{
            textTransform: "none",
            minHeight: "48px",
            borderRadius: "0px",
          }}
        >
          {submitButtonText}
        </Button>
      </Box>
    </form>
  );
};

export default ProfileForm;
